import React from 'react';

import SignUpBanner from '../components/Banners/SignUp';
import Layout from '../components/common/Layout';
import { MetaData } from '../components/common/meta';
import Hero from '../components/HomePage/Hero';
import { IPostCard, PostCard } from '../components/Post/Card';
import { PostGrid } from '../components/Post/Grid';
import { PopularPosts } from '../components/Post/Popular';
import GridIcon from '../images/GridIcon';

import style from '../styles/pages/index.scss';

import Loadable from '@loadable/component';
import { graphql, Link } from 'gatsby';

interface IIndexProps {
  data: {
    allGhostPost: {
      edges: {
        node: IPostCard;
      }[];
      totalCount: number;
    };
    allGhostPostFeatured: any;
    allGhostPageFeatured: any;
    allGhostPage: any;
  };
  location: {
    pathname: string;
  };
  pageContext: any;
}

/**
 * Main index page (home page)
 *
 * Loads all posts from Ghost and uses pagination to navigate through them.
 * The number of posts that should appear per page can be setup
 * in /utils/siteConfig.js under `postsPerPage`.
 *
 */

const BigCta = Loadable(() => import('../components/Banners/BigCta'));
const Pagination = Loadable(() => import('../components/common/Pagination'));

const Index = ({ data, location }: IIndexProps) => {
  const { allGhostPost, allGhostPage, allGhostPostFeatured, allGhostPageFeatured } = data;

  const allPostsAndPages = allGhostPost.edges.concat(allGhostPage.edges);

  const allPostsAndPagesFeatured = allGhostPostFeatured.edges.concat(allGhostPageFeatured.edges);

  const { postsPerPage } = require('../utils/siteConfig');

  const lastPage = Math.round(allGhostPost.totalCount / postsPerPage);

  const fakePageContext = {
    previousPagePath: '',
    nextPagePath: '/posts/page/2',
    humanPageNumber: 1,
    numberOfPages: lastPage,
  };

  const mqls =
    typeof window === 'undefined'
      ? []
      : [
          window.matchMedia('(max-width: 575px)'),
          window.matchMedia('(min-width: 576px) and (max-width: 1023px)'),
          window.matchMedia('(min-width: 1024px)'),
        ];

  const [perPage, setPerPage] = React.useState(3);

  function matchMediaQuery(): number {
    let perPage = 3;
    mqls.some((mql, index) => {
      if (mql.matches) {
        perPage = 3 * (index + 1);
        return true;
      }
      return false;
    });
    return perPage + 1; // 1 is used on "featured" card
  }

  function handleMediaQueryListener() {
    const newState = matchMediaQuery();
    if (perPage !== newState) {
      setPerPage(newState);
    }
  }

  React.useEffect(() => {
    mqls.forEach((mq) => {
      mq.addListener(handleMediaQueryListener);
    });

    handleMediaQueryListener();

    return () => {
      mqls.forEach((mq) => {
        mq.removeListener(handleMediaQueryListener);
      });
    };
  }, []);

  return (
    <>
      <MetaData
        location={location}
        data={{}}
        title="The Latest Personal Finance Content from Think Save Retire"
        description="Read the latest personal finance information on Think Save Retire including stories of reaching financial goals such as retiring by 40 or building wealth."
      />
      <Layout isHome>
        <Hero />
        <div className="container">
          <div className={style.mainPosts}>
            <div className="row">
              <div className="col-12 col-lg-8">
                <h2 className={style.heading}>Latest</h2>
                <PostCard
                  post={allPostsAndPages[0].node}
                  isExcerptShown
                  className={style.featuredPost}
                />
              </div>
              <div className="col-12 col-lg-4 order-first order-sm-first order-lg-last">
                <h2 className={style.heading}>
                  Popular
                  <Link to="/popular/">
                    <GridIcon /> Show All
                  </Link>
                </h2>
                <PopularPosts posts={allPostsAndPagesFeatured.slice(0, 3)} />
              </div>
            </div>
          </div>
        </div>
        <PostGrid posts={allPostsAndPages.slice(1, perPage)} isExcerptShown />
        <BigCta />
        <PostGrid posts={allPostsAndPages.slice(perPage, 2 * perPage - 1)} isExcerptShown />
        <div className="container">
          <Pagination pageContext={fakePageContext} />
        </div>
        <SignUpBanner className={style.signUpBanner} />
      </Layout>
    </>
  );
};

export default Index;

// This page query loads all posts sorted descending by published date
// The `limit` and `skip` values are used for pagination
export const pageQuery = graphql`
  query GhostPostQuery {
    allGhostPost(sort: { order: DESC, fields: [published_at, featured] }, limit: 13) {
      edges {
        node {
          slug
          feature_image
          excerpt
          title
          published_at
          primary_author {
            name
          }
          slug
          tags {
            name
            slug
          }
        }
      }
      totalCount
    }
    allGhostPage(
      sort: { order: DESC, fields: [published_at, featured] }
      filter: { authors: { elemMatch: { name: { ne: "Robot" } } } }
      limit: 13
    ) {
      edges {
        node {
          slug
          feature_image
          excerpt
          title
          published_at
          primary_author {
            name
          }
          slug
          tags {
            name
            slug
          }
        }
      }
      totalCount
    }
    allGhostPostFeatured: allGhostPost(
      sort: { order: DESC, fields: [published_at, featured] }
      filter: { featured: { eq: true } }
      limit: 3
    ) {
      edges {
        node {
          slug
          feature_image
          featured
          title
          published_at
          primary_author {
            name
          }
          slug
        }
      }
    }
    allGhostPageFeatured: allGhostPage(
      sort: { order: DESC, fields: [published_at, featured] }
      filter: { featured: { eq: true } }
      limit: 3
    ) {
      edges {
        node {
          slug
          feature_image
          featured
          title
          published_at
          primary_author {
            name
          }
          slug
        }
      }
    }
  }
`;
